import React, { useState, useCallback } from 'react';
import { Button, Box } from '@mui/material';
import { Save, Delete, Close, ContentCopy } from '@mui/icons-material';

interface DetailsEditorActions {
  onSave?: () => Promise<void> | void;
  onDelete?: () => Promise<void> | void;
  onCancel?: () => void;
}

interface DetailsEditorState {
  saveEnabled?: boolean;
  deleteEnabled?: boolean;
  saving?: boolean;
  deleting?: boolean;
}

interface DetailsEditorProps {
  children: React.ReactNode;
  title?: string;
  showSave?: boolean;
  showDelete?: boolean;
  showCancel?: boolean;
  showDuplicate?: boolean;
  onComplete?: (action: 'save' | 'delete' | 'cancel') => void;
}

const DetailsEditor: React.FC<DetailsEditorProps> = ({
  children,
  title,
  showSave = true,
  showDelete = true,
  showCancel = true,
  showDuplicate = false,
  onComplete
}) => {
  const [editorState, setEditorState] = useState<DetailsEditorState>({
    saveEnabled: true,
    deleteEnabled: true,
    saving: false,
    deleting: false
  });
  
  const [editorActions] = useState<DetailsEditorActions>({});
  
  const updateEditorState = useCallback((newState: Partial<DetailsEditorState>) => {
    setEditorState(prev => ({ ...prev, ...newState }));
  }, []);
  
  const handleSave = async () => {
    if (editorActions.onSave) {
      try {
        setEditorState(prev => ({ ...prev, saving: true }));
        await editorActions.onSave();
        onComplete?.('save');
      } catch (error) {
        console.error('Save failed:', error);
      } finally {
        setEditorState(prev => ({ ...prev, saving: false }));
      }
    }
  };
  
  const handleDelete = async () => {
    if (editorActions.onDelete) {
      try {
        setEditorState(prev => ({ ...prev, deleting: true }));
        await editorActions.onDelete();
        onComplete?.('delete');
      } catch (error) {
        console.error('Delete failed:', error);
      } finally {
        setEditorState(prev => ({ ...prev, deleting: false }));
      }
    } else {
      // If no delete action is set, just call onComplete
      onComplete?.('delete');
    }
  };
  
  const handleCancel = () => {
    if (editorActions.onCancel) {
      editorActions.onCancel();
    }
    onComplete?.('cancel');
  };
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {showSave && (
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={handleSave}
              disabled={!editorState.saveEnabled || editorState.saving}
            >
              {editorState.saving ? 'Saving...' : 'Save'}
            </Button>
          )}
          
          {showDuplicate && (
            <Button
              variant="outlined"
              startIcon={<ContentCopy />}
              onClick={() => {}}
              disabled={true}
            >
              Duplicate
            </Button>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          {showDelete && (
            <Button
              variant="contained"
              color="error"
              startIcon={<Delete />}
              onClick={handleDelete}
              disabled={!editorState.deleteEnabled || editorState.deleting}
            >
              {editorState.deleting ? 'Deleting...' : 'Delete'}
            </Button>
          )}
          
          {showCancel && (
            <Button
              variant="outlined"
              startIcon={<Close />}
              onClick={handleCancel}
            >
              Cancel
            </Button>
          )}
        </Box>
      </Box>
      
      <Box>
        {React.isValidElement(children) && 
          React.cloneElement(children, {
            editorActions,
            setEditorState: updateEditorState
          } as any)
        }
      </Box>
    </Box>
  );
};

export default DetailsEditor;